import { FC, useCallback, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import debounce from 'lodash.debounce';

import {
    Box,
    CardContent,
    LoadingButton,
    Typography,
} from '@thirdspacelearning/library';

import { isHTMLWhiteSpace, localStorage } from '../../../../utils';

import Dropzone from '../Dropzone';
import SlideContentLayout from './SlideContentLayout';
import LearningContentsField from './LearningContentsField';
import NotesTemplatePicker from './NotesTemplatePicker';

import { Template } from './templates';

import { createSlide, deleteSlide, editSlide } from '@api';
import { CurriculumQueryKeys } from '@services';
import { DeleteConfirmationDialog, QuillAdapter } from '@shared';
import { LearningObjective, Slide } from '@types';

const sx = {
    rightPanel: {
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'auto',
        flexGrow: 1,
    },
    formWrapper: {
        display: 'flex',
        height: '100%',
        width: '100%',
        position: 'relative',
        flexGrow: 1,
        '> form': {
            width: '100%',
        },
    },
};

interface Props {
    learningObjectiveId?: number;
    slide?: Slide;
    learningObjective: LearningObjective;
    fullScreen: boolean;
    hasUnsavedChanges: boolean;
    onClose: () => void;
    setSlideEdited: (edited: boolean) => void;
    changeTutorNotes: (tutorNotes: string, slide: Slide) => void;
}

const SlideEdit: FC<Props> = ({
    learningObjectiveId,
    slide = {} as Slide,
    learningObjective,
    fullScreen,
    hasUnsavedChanges,
    onClose,
    setSlideEdited,
    changeTutorNotes,
}) => {
    const queryClient = useQueryClient();

    const [unsavedChanges, setUnsavedChanges] = useState(hasUnsavedChanges);

    const { mutate: addSlide, isPending: isCreatingSlide } = useMutation({
        mutationFn: (data: any) =>
            createSlide(Number(learningObjectiveId), data),
        onSuccess: () => {
            enqueueSnackbar('Slide created successfully!', {
                key: 'create-slide-success',
                testId: 'create-slide-success',
                variant: 'curriculumSnackbar',
                severity: 'success',
            });

            queryClient.invalidateQueries({
                queryKey: [
                    `${CurriculumQueryKeys.SLIDES}-${learningObjectiveId}`,
                ],
            });

            onClose();
        },
        onError: (error: Error) => {
            enqueueSnackbar(error?.message, {
                title: 'There was an error creating the slide',
                key: 'create-slide-error',
                testId: 'create-slide-error',
                variant: 'curriculumSnackbar',
                severity: 'error',
            });
        },
    });

    const { mutate: updateSlide, isPending: isEditingSlide } = useMutation({
        mutationFn: (data: { slideId: number; data: any }) =>
            editSlide(data.slideId, Number(learningObjectiveId), data.data),
        onSuccess: () => {
            enqueueSnackbar('Slide edited successfully!', {
                key: 'edit-slide-success',
                testId: 'edit-slide-success',
                variant: 'curriculumSnackbar',
                severity: 'success',
            });

            queryClient.invalidateQueries({
                queryKey: [
                    `${CurriculumQueryKeys.SLIDES}-${learningObjectiveId}`,
                ],
            });

            onClose();
        },
        onError: (error: Error) => {
            enqueueSnackbar(error?.message, {
                title: 'There was an error editing the slide',
                key: 'edit-slide-error',
                testId: 'edit-slide-error',
                variant: 'curriculumSnackbar',
                severity: 'error',
            });
        },
    });

    const { mutate: removeSlide, isPending: isDeletingSlide } = useMutation({
        mutationFn: (slideId: number) =>
            deleteSlide(slideId, Number(learningObjectiveId)),
        onSuccess: () => {
            enqueueSnackbar('Slide edited successfully!', {
                key: 'edit-slide-success',
                testId: 'edit-slide-success',
                variant: 'curriculumSnackbar',
                severity: 'success',
            });

            queryClient.invalidateQueries({
                queryKey: [
                    `${CurriculumQueryKeys.SLIDES}-${learningObjectiveId}`,
                ],
            });

            onClose();
        },
        onError: (error: Error) => {
            enqueueSnackbar(error?.message, {
                title: 'There was an error editing the slide',
                key: 'edit-slide-error',
                testId: 'edit-slide-error',
                variant: 'curriculumSnackbar',
                severity: 'error',
            });
        },
    });

    const onSubmit = async (
        values: Slide,
    ): Promise<{ [FORM_ERROR]: string } | void> => {
        if (learningObjectiveId) {
            if (slide && slide.id) {
                setSlideEdited(false);
                setUnsavedChanges(false);
                localStorage.remove(`slide-${slide.id}`);

                updateSlide({ slideId: slide.id, data: values });
            } else {
                addSlide(values);
            }
        }
    };

    const onDelete = async (): Promise<{ [FORM_ERROR]: string } | void> => {
        if (learningObjectiveId && 'id' in slide) {
            removeSlide(slide.id);
        }
    };

    const slideTypeField = (
        <Box my={2}>
            <Typography component="div">Steps</Typography>
            <LearningContentsField
                learningContents={learningObjective.learning_contents}
            />
        </Box>
    );

    const onTemplateSelected = (template: Template, values: Slide) => {
        const { tutor_notes } = values;
        if (
            tutor_notes &&
            !(
                isHTMLWhiteSpace(slide.tutor_notes) ||
                isHTMLWhiteSpace(tutor_notes)
            )
        ) {
            const confirmed = window.confirm(
                'You will lose the contents of your tutor notes - are you sure you wish to proceed?',
            );

            if (confirmed) {
                changeTutorNotes(template.content, values);
            }
        } else {
            changeTutorNotes(template.content, values);
        }
    };

    const slideEdited = (dirty: boolean, values: Slide) => {
        const { tutor_notes } = values;
        localStorage.save(`slide-${slide.id}`, tutor_notes);
        setSlideEdited(dirty);
    };

    const debouncedSlideEditedHandler = useCallback(
        debounce(slideEdited, 300),
        [],
    );

    useEffect(() => {
        setUnsavedChanges(hasUnsavedChanges);
    }, [hasUnsavedChanges]);

    return (
        <Form<Slide>
            onSubmit={onSubmit}
            initialValues={slide}
            render={({
                handleSubmit,
                submitting,
                pristine,
                dirty,
                submitError,
                values,
            }) => (
                <Box sx={sx.formWrapper}>
                    <form
                        onSubmit={handleSubmit}
                        onKeyUp={() =>
                            debouncedSlideEditedHandler(dirty ?? false, values)
                        }
                    >
                        <SlideContentLayout
                            left={
                                <Box
                                    flexGrow={1}
                                    position="relative"
                                    display="flex"
                                    flexDirection="column"
                                    minWidth="50%"
                                    pl={1}
                                >
                                    <Field
                                        name="image_path"
                                        buttonLabel={
                                            'id' in slide
                                                ? 'Click or drag to replace image (.png)'
                                                : 'Click or drag to add image (.png)'
                                        }
                                        showPreview
                                        validate={(value) =>
                                            value
                                                ? undefined
                                                : 'Please upload an image'
                                        }
                                    >
                                        {(props) => <Dropzone {...props} />}
                                    </Field>
                                    {!!fullScreen && slideTypeField}
                                </Box>
                            }
                            right={
                                <CardContent sx={sx.rightPanel}>
                                    <Box>
                                        <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                            mb={2}
                                        >
                                            <LoadingButton
                                                type="submit"
                                                color="primary"
                                                loading={
                                                    isCreatingSlide ||
                                                    isEditingSlide ||
                                                    isDeletingSlide
                                                }
                                                disabled={
                                                    isCreatingSlide ||
                                                    isEditingSlide ||
                                                    isDeletingSlide ||
                                                    ((pristine ?? true) &&
                                                        !unsavedChanges)
                                                }
                                            >
                                                Save
                                            </LoadingButton>
                                        </Box>
                                        {'objective_type' in
                                            learningObjective && (
                                            <NotesTemplatePicker
                                                onTemplateSelect={(template) =>
                                                    onTemplateSelected(
                                                        template,
                                                        values,
                                                    )
                                                }
                                                selectedType={
                                                    learningObjective.objective_type
                                                }
                                            />
                                        )}
                                        <Typography component="div">
                                            Tutor Notes
                                        </Typography>
                                        <Field
                                            name="tutor_notes"
                                            component={QuillAdapter}
                                        />
                                        <Field
                                            name="components_path"
                                            buttonLabel={
                                                'id' in slide
                                                    ? 'Click or drag to replace the component file (.zip)'
                                                    : 'Click or drag to add the component file (.zip)'
                                            }
                                            showDetails
                                        >
                                            {(props) => <Dropzone {...props} />}
                                        </Field>
                                        {!fullScreen && slideTypeField}
                                    </Box>
                                    {onDelete && 'id' in slide && (
                                        <Box
                                            display="flex"
                                            justifyContent="flex-end"
                                        >
                                            <DeleteConfirmationDialog
                                                onDelete={onDelete}
                                                message={
                                                    'Are you sure you want to delete this slide?'
                                                }
                                            />
                                        </Box>
                                    )}
                                    {submitError && (
                                        <Typography
                                            variant="subtitle1"
                                            color="error"
                                            align="center"
                                            paragraph
                                        >
                                            {submitError}
                                        </Typography>
                                    )}
                                </CardContent>
                            }
                        />
                    </form>
                </Box>
            )}
        />
    );
};

export default SlideEdit;
